import type { MechanicDefinition } from "./types";

/**
 * Reactive World — Unified mechanic merging:
 * - Consequence Engine (choice tracking & delayed consequences)
 * - Butterfly Engine (narrative seed system)
 * - World Reactivity (living world simulation)
 */
export const reactiveWorld: MechanicDefinition = {
  id: "reactive_world",
  name: "Reactive World",
  description:
    "Living world engine: player choices create tracked consequences, narrative seeds incubate into events, and the world evolves in response to player actions. Uses extra AI credits for background seed analysis.",
  icon: "Globe",
  category: "core",
  aiHints: {
    purpose:
      "Unified world-response system: consequences ripple, narrative seeds incubate and hatch, NPCs act off-screen, factions pursue agendas.",
    whenToUse:
      "Any narrative-driven world where player agency, living-world feel, and forward-planning consequences matter.",
    synergies: ["encounter_tables", "reputation", "morality", "quest_journal", "time_passage"],
    conflicts: [],
    prerequisites: [],
    tags: [
      "consequences", "seeds", "butterfly", "living-world",
      "reactivity", "narrative-intelligence", "forward-planning",
    ],
    complexity: 3,
    auditPasses: 1,
    exampleConfig: {
      reactivity_level: "responsive",
      consequence_severity: "meaningful",
      telegraph: true,
      delay_turns: 5,
      npc_autonomy: true,
      auto_seed: true,
      plot_aware: true,
      max_active_threads: 15,
      thread_complexity: "balanced",
      seed_creativity: "balanced",
      foreshadow: true,
    },
    genrePresets: {
      forgiving_adventure: {
        reactivity_level: "responsive",
        consequence_severity: "forgiving",
        telegraph: true,
        delay_turns: 8,
        auto_seed: true,
        plot_aware: true,
      },
      dark_souls: {
        reactivity_level: "living",
        consequence_severity: "permanent",
        telegraph: false,
        delay_turns: 3,
        auto_seed: true,
        plot_aware: true,
      },
    },
    stateVariables: [
      "_pending_consequences", "_consequence_history",
      "_world_events_queue", "_npc_offscreen_actions", "_faction_agendas",
      "_proactive_events", "_inspector_pacing_note", "_director_briefing",
    ],
  },

  configSchema: [
    // ── World Reactivity ──
    {
      key: "reactivity_level",
      label: "World Reactivity",
      type: "select",
      options: [
        { value: "static", label: "Static (world is a backdrop)" },
        { value: "responsive", label: "Responsive (reacts to major events)" },
        { value: "living", label: "Living (world evolves independently)" },
      ],
      default: "responsive",
    },
    {
      key: "npc_autonomy",
      label: "NPC Autonomy",
      type: "boolean",
      default: true,
      description: "NPCs pursue their own goals off-screen",
    },
    // ── Consequence Engine ──
    {
      key: "consequence_severity",
      label: "Consequence Weight",
      type: "select",
      options: [
        { value: "forgiving", label: "Forgiving (second chances exist)", description: "Consequences are real but rarely catastrophic." },
        { value: "meaningful", label: "Meaningful (choices matter)", description: "Consequences are lasting but some can be reversed with effort." },
        { value: "permanent", label: "Permanent (no take-backs)", description: "Every decision is final and irreversible." },
      ],
      default: "meaningful",
    },
    {
      key: "telegraph",
      label: "Telegraph Consequences",
      type: "boolean",
      default: true,
      description: "Hint at potential consequences before the player commits.",
    },
    {
      key: "delay_turns",
      label: "Delayed Consequence Window",
      type: "number",
      default: 5,
      description: "How many turns before a delayed consequence surfaces.",
    },
    // ── Butterfly Engine ──
    {
      key: "auto_seed",
      label: "Auto-Seed (Phase 3)",
      type: "boolean",
      default: true,
      description: "Automatically plant 0-2 seeds per turn using a background AI pass. Uses extra credits.",
    },
    {
      key: "plot_aware",
      label: "Plot-Aware Agent",
      type: "boolean",
      default: true,
      description: "Cross-reference world plot entries to create narrative connections between seeds and planned story events.",
    },
    {
      key: "max_active_threads",
      label: "Max Active Threads",
      type: "number",
      default: 15,
      description: "Maximum narrative threads tracked simultaneously (up to 30). Higher = richer but more credit-intensive.",
    },
    {
      key: "causality_tracking",
      label: "Causality Tracking",
      type: "boolean",
      default: true,
      description: "Track the origin of narrative threads (Player, Plot, or Emergent) for deeper historical context.",
    },
    {
      key: "emergent_thread_frequency",
      label: "Emergent Frequency",
      type: "select",
      options: [
        { value: "rare", label: "Rare (background events are sparse)" },
        { value: "balanced", label: "Balanced (world consistently feels alive)" },
        { value: "frequent", label: "Frequent (constant off-stage developments)" },
      ],
      default: "balanced",
      description: "How often the AI creates independent 'Emergent' threads not directly tied to the player or plot.",
    },
    {
      key: "thread_complexity",
      label: "Thread Complexity",
      type: "select",
      options: [
        { value: "simple", label: "Simple (fewer, clearer threads)" },
        { value: "balanced", label: "Balanced (moderate weaving)" },
        { value: "epic", label: "Epic (many interwoven threads, deep connections)" },
      ],
      default: "balanced",
      description: "How many narrative threads to maintain and how deeply to interconnect them.",
    },
    {
      key: "seed_creativity",
      label: "Seed Creativity",
      type: "select",
      options: [
        { value: "conservative", label: "Conservative (grounded, predictable)" },
        { value: "balanced", label: "Balanced (mix of expected and surprising)" },
        { value: "wild", label: "Wild (unexpected connections, dramatic twists)" },
      ],
      default: "balanced",
      description: "How creative and surprising the butterfly agent's narrative seeds can be.",
    },
    {
      key: "foreshadow",
      label: "Foreshadowing",
      type: "boolean",
      default: true,
      description: "Allow the narrator to subtly foreshadow brewing seeds.",
    },
    {
      key: "initial_threads",
      label: "Initial Narrative Threads",
      type: "thread_list",
      default: [],
      description: "Pre-define narrative seeds that will be planted when an adventure starts. These give the butterfly engine a head start with world-specific storylines.",
    },
  ],

  systemPromptSection: (config) => {
    const level = config.reactivity_level || "responsive";
    const autonomy = config.npc_autonomy !== false;
    const severity = config.consequence_severity || "meaningful";
    const telegraph = config.telegraph !== false;
    const delay = config.delay_turns || 5;
    const foreshadow = config.foreshadow !== false;
    const autoSeed = config.auto_seed !== false;
    const plotAware = config.plot_aware !== false;

    return `## REACTIVE WORLD (Core — Unified World Response System)

### World Reactivity
- Level: ${level}. NPC Autonomy: ${autonomy ? "enabled" : "disabled"}.
- ${level === "static" ? "The world is a stage for the player's story. Things don't change unless the player causes it." : level === "responsive" ? "The world reacts to significant player actions. Kill a bandit leader? The roads become safer. Burn a forest? The ecosystem shifts." : "The world evolves on its own. Factions pursue agendas. Seasons change. Events happen off-screen. The player is part of a living world, not the center of it."}
- ${autonomy ? "NPCs have goals and take actions off-screen. When the player returns to a location, things may have changed. Reference what NPCs have been doing." : "NPCs only act in response to the player."}
- Describe environmental changes that reflect past events. News and rumors spread organically.
${level === "living" ? "- A WORLD SIMULATION TICK runs between turns, progressing faction agendas and NPC offscreen activities. Reference _npc_offscreen_actions and _faction_agendas organically." : ""}

### Consequence Engine
- Weight: ${severity}. Telegraph: ${telegraph ? "yes" : "no"}. Delay window: ${delay} turns.
- Every significant player choice must create consequences that ripple through the story.
- ${severity === "forgiving" ? "Consequences are real but rarely catastrophic. Players can find ways to mitigate bad outcomes." : severity === "permanent" ? "Consequences are irreversible. Dead NPCs stay dead. Burned bridges stay burned. Make this clear." : "Consequences are meaningful and lasting but not always permanent. Some can be reversed with effort."}
- ${telegraph ? "Before major decisions, subtly hint at potential consequences through NPC warnings, environmental cues, or intuition." : "Don't telegraph consequences. Let the player discover results naturally."}
- MANDATORY: Use record_consequence tool after significant player choices. Set is_pending=true for delayed effects.
- MANDATORY: The postTurnHook checks pending consequences every turn. When delay_turns have passed, use reveal_delayed_consequence.
- Track consequence chains: action → reaction → escalation. Small choices compound.
- NPCs gossip — actions in one area spread to others over time. Create callback moments.

### 🦋 Butterfly Engine V2 (Narrative Thread Manager)
- You have two tools: \`plant_narrative_seed\` and \`hatch_seed\`.
- **plant_narrative_seed**: Plant a future consequence, NPC agenda, faction plan, rumor, social dynamic, cultural exchange, or discovery. Seeds progress through phases: PLANTED → GROWING → RIPENING → READY.
- **hatch_seed**: When a seed reaches READY phase (shown as "READY" in context), weave it in and call hatch_seed to retire it.
- Seeds are INVISIBLE to the player — your internal planning scratchpad.
- Plant 1-2 seeds per significant player action. Think: "What butterfly effects could this create?"
- Categories: consequence, npc_agenda, faction_plan, rumor, environmental, discovery, social, cultural.
- A background V2 agent manages thread lifecycle — evolving seeds through phases, pruning stale ones, and connecting them to plot entries. You'll see evolution notes in seed context.
- Before generating random encounters, CHECK if any RIPENING or READY seeds can be used instead.
- Use seeds to give encounters meaning, history, and player-driven causality.
${foreshadow ? "- You MAY subtly foreshadow GROWING/RIPENING seeds through rumors, omens, or environmental hints. Do NOT reveal the seed itself." : "- Do NOT foreshadow or hint at brewing seeds."}
${autoSeed ? "- The Butterfly V2 background agent plants and evolves seeds automatically — you'll see them with phase tags in your seed context." : ""}
${plotAware ? `- DELIVERY INSTRUCTIONS: When you see a 🎯 DELIVERY INSTRUCTION in your seed context, follow it to weave the seed into your narration. These connect player-caused seeds with planned plot events for maximum narrative impact.
- After delivering a seed with instructions, call \`hatch_seed\` to retire it.` : ""}

### 🎬 Situation Inspector (Proactive Event Director)
- A background Situation Inspector analyzes the story after each turn and may schedule **proactive events** — encounters, revelations, complications, opportunities, atmosphere shifts, NPC actions, or world changes.
- When you see a **DIRECTOR'S BRIEFING** section with ⚡ DELIVER THIS TURN events, you MUST weave them into your response. These are pre-planned dramatic beats from the story director.
- Each event includes narrator_guidance with specific direction — follow it closely.
- Events marked 🔮 UPCOMING are for foreshadowing awareness only. You may hint at them through atmosphere or NPC behavior, but do NOT deliver them yet.
- Proactive events exist to make the world feel alive and self-driven, not just reactive to the player's prompts. They represent things that would happen in this world regardless of the player's actions.`;
  },

  widgets: [
    {
      id: "decision_journal",
      type: "sidebar_tab",
      label: "Decisions",
      icon: "GitBranch",
      component: "DecisionJournal",
      dataBindings: {
        _consequence_history: "variables._consequence_history",
        _pending_consequences: "variables._pending_consequences",
      },
      visibility: "when_data",
      priority: 52,
    },
  ],

  tools: [
    {
      type: "function",
      function: {
        name: "record_consequence",
        description: "Record a player choice and its consequence for the Decision Journal.",
        parameters: {
          type: "object",
          properties: {
            id: { type: "string", description: "Unique ID for this consequence entry" },
            choice: { type: "string", description: "What the player chose to do" },
            immediate_outcome: { type: "string", description: "What happened immediately" },
            severity: { type: "string", description: "minor, moderate, or major" },
            is_pending: { type: "boolean", description: "Whether delayed effects are still pending" },
          },
          required: ["id", "choice", "immediate_outcome"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "reveal_delayed_consequence",
        description: "Surface a delayed consequence from an earlier choice.",
        parameters: {
          type: "object",
          properties: {
            consequence_id: { type: "string", description: "ID of the original consequence entry" },
            description: { type: "string", description: "What delayed effect just surfaced" },
          },
          required: ["consequence_id", "description"],
        },
      },
    },
  ],

  initializeState: () => ({
    // Consequence Engine
    _pending_consequences: [],
    _consequence_history: [],
    // World Reactivity
    _world_events_queue: [],
    _npc_offscreen_actions: [],
    _faction_agendas: [],
    _last_sim_tick_turn: 0,
    _sim_tick_count: 0,
    _npc_relationships: {},
    _scene_senses: null,
    _scene_id: 0,
    _prev_location: "",
    _conversation_summaries: [],
    _last_compression_turn: 0,
    // Butterfly Engine
    _seeds_planted: 0,
    _seeds_hatched: 0,
    // Situation Inspector (proactive events from butterfly agent)
    _proactive_events: [],
    _inspector_pacing_note: "",
    _director_briefing: "",
  }),

  postTurnHook: (context) => {
    const vars = context.state.variables || {};
    const notes: string[] = [];

    // ── Consequence delay checking ──
    const pending = vars._pending_consequences || [];
    const currentTurn = vars._current_turn || 0;
    const delayWindow = context.config.delay_turns || 5;

    for (const conseq of pending) {
      const turnsSince = currentTurn - (conseq.turn_created || 0);
      if (turnsSince >= delayWindow) {
        notes.push(`⚡ DELAYED CONSEQUENCE READY: "${conseq.choice}" (${turnsSince} turns ago) — use reveal_delayed_consequence now.`);
      } else if (turnsSince >= Math.floor(delayWindow / 2)) {
        notes.push(`⏳ Consequence "${conseq.id}" approaching — begin foreshadowing.`);
      }
    }

    return notes.length > 0 ? { narrativeNotes: notes } : null;
  },
};
